<template>
  <AuthLayout>
    <div class="tw:flex tw:flex-col tw:gap-1.5">
      <h1 class="tw:m-0! tw:text-30! tw:font-bold! tw:tracking-[-0.02em]! tw:text-gray-900!">
        {{ $t('auth.welcome_back') }}
      </h1>
      <p class="tw:m-0 tw:text-14 tw:text-gray-600">{{ $t('auth.welcome_subtitle') }}</p>
    </div>

    <!-- TODO: enable once the backend supports Google sign-in
    <LeadButton variant="white" block :icon="IconBrandGoogle">Continue with Google</LeadButton>
    <div class="tw:flex tw:items-center tw:gap-3">
      <span class="tw:h-px tw:flex-1 tw:bg-border-secondary"></span>
      <span class="tw:text-12 tw:text-muted">or continue with email</span>
      <span class="tw:h-px tw:flex-1 tw:bg-border-secondary"></span>
    </div>
    -->

    <form class="tw:flex tw:flex-col tw:gap-6" novalidate @submit.prevent="signInUser">
      <div class="tw:flex tw:flex-col tw:gap-4">
        <LeadInput
          v-model="email"
          type="email"
          autocomplete="username"
          placeholder="your@email.com"
          data-testid="login-email"
          :label="emailLabel"
          :leading-icon="IconMail"
          :errors="signInErrors?.identifier ?? []" />
        <LeadInput
          v-model="password"
          type="password"
          autocomplete="current-password"
          data-testid="login-password"
          :label="$t('password')"
          :placeholder="$t('login.password_placeholder')"
          :leading-icon="IconLock"
          :errors="signInErrors?.secret ?? []" />
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
          <!-- TODO: not wired to the session; needs backend support for a persistent sign-in -->
          <LeadCheckbox v-model="keepSignedIn" :label="$t('auth.keep_signed_in')" data-testid="login-keep-signed-in" />
          <RouterLink
            v-if="allowPasswordReset"
            :to="{ name: 'forgotPassword' }"
            class="tw:text-13 tw:auth-link"
            data-testid="login-forgot-password">
            {{ $t('forgot_password.title') }}?
          </RouterLink>
        </div>
      </div>

      <Transition name="login-error">
        <div
          v-if="formError"
          class="tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-13 tw:text-red-600"
          role="alert"
          data-testid="login-error">
          {{ formError }}
        </div>
      </Transition>

      <div class="tw:flex tw:flex-col tw:gap-4">
        <LeadButton type="submit" block :loading="isSubmitting" data-testid="login-submit">
          {{ $t('sign_in') }}
        </LeadButton>
        <p v-if="allowSelfRegistration" class="tw:m-0 tw:text-center tw:text-14 tw:text-gray-600">
          {{ $t('auth.new_to_app', { app: brand.shortName }) }}
          <RouterLink :to="{ name: 'registration' }" class="tw:auth-link" data-testid="login-sign-up">
            {{ $t('auth.create_account') }}
          </RouterLink>
        </p>
      </div>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
import { brand } from '@/config/brands';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IconLock, IconMail } from '@tabler/icons-vue';

import { allowSelfRegistration } from '@/config/app';
import { requestUserSignIn } from '@/api/auth';
import type { ClientError } from '@/api/client';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import translator from '@/locale/translator';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadCheckbox from '@/components/lead-design/LeadCheckbox.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();

const allowPasswordReset = true;

/* ---------------------------------- STATE ---------------------------------- */
const email = ref('');
const password = ref('');
const keepSignedIn = ref(false);
const isSubmitting = ref(false);
const signInErrors = ref<{ [index: string]: string[] }>();
const formError = ref('');

/* -------------------------------- COMPUTED --------------------------------- */
const emailLabel = computed(() =>
  brand.appType === 'HRIQ' ? translator.get('email_address') : translator.get('auth.work_email')
);

/* --------------------------------- METHODS --------------------------------- */
function isSafeRedirect(target: unknown): target is string {
  return typeof target === 'string' && target.startsWith('/') && !target.startsWith('//') && !target.includes('://');
}

function signInUser() {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  signInErrors.value = undefined;
  formError.value = '';

  requestUserSignIn(email.value, password.value)
    .then(() => {
      userStore.fetchCurrent().then(async () => {
        // features are meaningless mid-registration and the request would be refused
        if (!userStore.hasUnfinishedRegistration) await featureStore.fetchFeatures();

        // an unfinished registration resumes in the wizard; a finished one returns to where it was headed
        if (userStore.hasUnfinishedRegistration) {
          router.push(userStore.authenticatedLandingRoute());
          return;
        }

        const redirect = route.query.redirect;
        router.push(isSafeRedirect(redirect) ? redirect : { name: 'home' });
      });
    })
    .catch((error: ClientError) => {
      signInErrors.value = error.responseJson.errors ?? [];
      // TODO: show attempts left / lockout here if the API starts returning it
      formError.value = error.responseJson.message ?? translator.get('auth.invalid_credentials');
    })
    .finally(() => {
      isSubmitting.value = false;
    });
}
</script>

<style scoped>
.login-error-enter-active,
.login-error-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.login-error-enter-from,
.login-error-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .login-error-enter-active,
  .login-error-leave-active {
    transition: none;
  }
}
</style>
